import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ErrorStateMatcher} from '@angular/material/core';
import { TagGroup, Tag, MODEL_TAG_GROUP_KEY, getUnsortedModelList } from '../../common/organisation';
import { getColourStyleForTagIndex, getIconForTagIndex, MAX_COLOURS, nextTagIndex } from './tag-colours';
import { v4 } from 'uuid';
import { OrganisationService } from '../organisation.service';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
    selector: 'tag-group-dialog',
    styleUrls: ['./tag-group-dialog.css'],
    templateUrl: 'tag-group-dialog.html',
    standalone: false
})
export class TagGroupDialogComponent {
    tagGroup: TagGroup
    extended:boolean
    tagGroupForm: FormGroup
    matcher = new MyErrorStateMatcher();
    namePlaceholder: string

    displayedColumns: string[] = ['name', 'email', 'role'];

    MAX_COLOURS = MAX_COLOURS

    constructor(public dialogRef: MatDialogRef<TagGroupDialogComponent>, private formBuilder: FormBuilder, private organisationService: OrganisationService,
        @Inject(MAT_DIALOG_DATA) data:{tagGroup: TagGroup, extended:boolean}) {

        this.tagGroup = data.tagGroup
        this.extended = data.extended
        if (!this.tagGroup.tags) this.tagGroup.tags = []

        this.tagGroupForm  =  this.formBuilder.group({
            name: [this.tagGroup.name, [Validators.required, Validators.minLength(3), Validators.maxLength(30)]],
            description: [this.tagGroup.description, Validators.maxLength(50)],
            modelKeys: [this.tagGroup.modelKeys],
            deleteField: [""],
            tags: this.formBuilder.array(this.tagGroup.tags.map(t => {
                return this.buildTag(t)
            }))
        })
    }

    get tags(): FormArray { return this.tagGroupForm.get('tags') as FormArray; }

    areModelTags() {
        return this.tagGroup._key === MODEL_TAG_GROUP_KEY
    }

    buildTag(tag:Tag) {
        return this.formBuilder.group({
            _key: [tag._key],
            name: [tag.name, [Validators.required, Validators.minLength(1), Validators.maxLength(40)]],
            description: [tag.description, Validators.maxLength(50)],
            tagIndex: [tag.tagIndex, [Validators.required]],
            imageUrl: [tag.imageUrl, Validators.maxLength(200)]
        })
    }

    addTag(): void {
        if (this.tags.length < MAX_COLOURS) {
            const newTag = {_key:v4(), name:'', description:'', tagIndex:this.getNextTagIndex()} as Tag
            this.tags.push(this.buildTag(newTag));
        }
    }

    getNextTagIndex():number {
        return nextTagIndex(this.tags.controls.map(t => t.get('tagIndex').value))
    }

    tagError(i:number) {
        return this.tags.controls[i].get('name').invalid
    }

    getModels() {
        return getUnsortedModelList(this.organisationService.organisation).sort((a,b) => a.name.toLowerCase() > b.name.toLowerCase()?1:-1)
    }

    removeTag(i: number): void {
        this.tags.removeAt(i);
    }

    onCancelClick(): void {
        this.dialogRef.close();
    }

    getColourStyleForTag(i: number) {
        if (this.tags.at(i).get('tagIndex')) return getColourStyleForTagIndex(this.tags.at(i).get('tagIndex').value)
        return ""
    }

    getIconLocation(tag:Tag) {
        return getIconForTagIndex(tag.tagIndex)
    }

    deleteGroup() {
        this.tagGroup.deleted = true
        this.dialogRef.close(this.tagGroup);
    }

    deleteDisabled() {
        return this.tagGroupForm.value.deleteField.toLocaleLowerCase() !== "delete"
    }

    // only change the tag group once the form is valid, as it is shown elsewhere while the dialog is open
    save() {
        this.tagGroupForm.markAllAsTouched()
        if (this.tagGroupForm.invalid) return

        this.tagGroup.name = this.tagGroupForm.value.name
        this.tagGroup.description = this.tagGroupForm.value.description
        if (!this.areModelTags())
            this.tagGroup.modelKeys = this.tagGroupForm.value.modelKeys

        this.tagGroup.tags = this.tagGroupForm.value.tags.map(t => {
            return t
        })

        if (this.tagGroupForm.valid) this.dialogRef.close(this.tagGroup);
    }
}
